import * as React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Plus, Check, X, CalendarDays, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { useAuth } from "@/context/AuthContext";
import DetailDrawer from "@/components/DetailDrawer";
import RecordsPanel from "@/components/records/RecordsPanel";
import { toast } from "sonner";
import { SkeletonTable } from "@/components/feel/Skeleton";
import { EmptyState, ErrorState } from "@/components/feel/EmptyState";

const LEAVE_TYPES = [
  { key: "holiday", label: "Holiday" },
  { key: "sick",    label: "Sick" },
  { key: "unpaid",  label: "Unpaid" },
];

const STATUS_CHIP = {
  pending:  "chip",
  approved: "chip chip-emerald",
  denied:   "chip chip-coral",
};

export default function HR() {
  const { companyId } = useParams();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const { has, entitlements } = useCompany();
  const tab = params.get("tab") || "leave";

  if (!entitlements?.modules?.hr) {
    return <div className="p-6 text-brand-muted text-[12px]" data-testid="hr-disabled">
      HR module is disabled for this company.
    </div>;
  }

  return (
    <div className="px-4 md:px-6 py-4 md:py-6 max-w-[1200px]" data-testid="hr-page">
      <div className="flex items-center gap-1 border-b border-brand-rule mb-4">
        <HRTab v="leave" cur={tab} setV={(v) => setParams({ tab: v })} icon={<CalendarDays size={13}/>}>
          Leave
        </HRTab>
        <HRTab v="records" cur={tab} setV={(v) => setParams({ tab: v })} icon={<ShieldCheck size={13}/>}>
          Records
        </HRTab>
      </div>
      {tab === "leave"   && <LeaveTab />}
      {tab === "records" && <RecordsTab />}
    </div>
  );
}

function HRTab({ v, cur, setV, icon, children }) {
  const active = v === cur;
  return (
    <button onClick={() => setV(v)}
            className={`px-3 py-2 text-[12.5px] font-medium border-b-2 flex items-center gap-1.5 ${
              active ? 'border-brand-violet text-brand-violet' : 'border-transparent text-brand-muted hover:text-brand-ink'}`}
            data-testid={`hr-tab-${v}`}>
      {icon}{children}
    </button>
  );
}

function RecordsTab() {
  const { companyId } = useParams();
  const { has } = useCompany();
  return (
    <div data-testid="hr-records-tab">
      <div className="mb-3">
        <div className="micro-label">Compliance</div>
        <h2 className="mt-1 text-[18px] font-bold tracking-tight text-brand-ink">Expiring records</h2>
        <div className="mt-1 text-[12px] text-brand-muted">
          Licences, certificates, insurances. Blocking records halt shift assignment when lapsed;
          non-blocking ones surface as advisories.
        </div>
      </div>
      <RecordsPanel companyId={companyId} scope="company" canEdit={has("hr.manage")} testidPrefix="hr-records" />
    </div>
  );
}

function LeaveTab() {
  const { companyId } = useParams();
  const { user } = useAuth();
  const { has } = useCompany();
  const [rows, setRows] = React.useState(null);
  const [statusFilter, setStatusFilter] = React.useState("");
  const [drawer, setDrawer] = React.useState(null); // leave row
  const [reqOpen, setReqOpen] = React.useState(false);
  const [loadError, setLoadError] = React.useState(false);
  const canManage = has("hr.manage");
  const canView = has("hr.view_all");

  const load = React.useCallback(async () => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    try {
      const { data } = await api.get(`/companies/${companyId}/hr/leave`, { params });
      setRows(data.leave);
      setLoadError(false);
    } catch (e) {
      setLoadError(true);
      setRows([]);
    }
  }, [companyId, statusFilter]);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    const on = (e) => { const t = e.detail?.type; if (t && t.startsWith("leave.")) load(); };
    window.addEventListener("fable:event", on);
    return () => window.removeEventListener("fable:event", on);
  }, [load]);

  async function decide(row, status) {
    try {
      await api.patch(`/companies/${companyId}/hr/leave/${row.id}`, { status });
      toast.success(status === "approved" ? "Approved" : "Denied");
      setDrawer(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }

  if (rows === null) {
    return (
      <div data-testid="hr-loading">
        <div className="micro-label">People</div>
        <h2 className="mt-1 text-[18px] font-bold tracking-tight text-brand-ink">Leave</h2>
        <div className="mt-4"><SkeletonTable rows={6} cols={5} /></div>
      </div>
    );
  }

  return (
    <div data-testid="hr-leave-tab">
      <div className="flex items-start justify-between">
        <div>
          <div className="micro-label">People</div>
          <h2 className="mt-1 text-[18px] font-bold tracking-tight text-brand-ink">Leave requests</h2>
          <div className="mt-1 text-[12px] text-brand-muted">
            <span className="mono-num text-brand-ink">{rows.length}</span>
            {canView ? " company-wide" : " (yours only)"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select className="field-input !w-40 !h-8" value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  data-testid="hr-filter-status">
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="denied">Denied</option>
          </select>
          <button className="btn-violet" onClick={() => setReqOpen(true)} data-testid="hr-new-leave">
            <Plus size={13} /> Request leave
          </button>
        </div>
      </div>

      <div className="mt-4 card-panel overflow-hidden">
        <div className="table-scroll"><table className="fable-table" data-testid="hr-leave-table">
          <thead>
            <tr>
              <th style={{width: "20%"}}>Person</th>
              <th style={{width: "12%"}}>Type</th>
              <th style={{width: "22%"}}>Dates</th>
              <th style={{width: "12%"}}>Status</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-clickable="true" onClick={() => setDrawer(r)}
                  data-testid={`leave-row-${r.id}`}>
                <td>
                  <span className="font-medium text-brand-ink">{r.user_name}</span>
                  {r.user_id === user?.id && (
                    <span className="ml-1.5 mono-num text-[9px] chip chip-violet">you</span>
                  )}
                </td>
                <td><span className="chip">{r.leave_type}</span></td>
                <td className="mono-num text-[11.5px] text-brand-muted">
                  {r.start_date}{r.start_date === r.end_date ? "" : ` → ${r.end_date}`}
                </td>
                <td><span className={STATUS_CHIP[r.status] || "chip"}>{r.status}</span></td>
                <td className="text-[12px] text-brand-ink truncate max-w-[260px]">{r.note}</td>
              </tr>
            ))}
            {rows.length === 0 && !loadError && (
              <tr><td colSpan={5} className="p-0">
                <EmptyState icon={CalendarDays} eyebrow="No leave"
                  title={statusFilter ? `No ${statusFilter} requests.` : "No leave requests yet."}
                  body="When someone requests time off it appears here."
                  testid="hr-leave-empty" />
              </td></tr>
            )}
            {loadError && (
              <tr><td colSpan={5} className="p-0">
                <ErrorState title="Couldn't load leave" body="The request failed. Try again in a moment."
                  onRetry={load} testid="hr-leave-error" />
              </td></tr>
            )}
          </tbody>
        </table></div>
      </div>

      {/* Request leave drawer */}
      <DetailDrawer open={reqOpen} onOpenChange={setReqOpen}
                    title="Request leave" subtitle="Time off for yourself"
                    testid="hr-request-drawer">
        <RequestLeaveForm companyId={companyId} onDone={() => { setReqOpen(false); load(); }} />
      </DetailDrawer>

      {/* Decide drawer */}
      <DetailDrawer open={!!drawer} onOpenChange={(v) => { if (!v) setDrawer(null); }}
                    title={drawer?.user_name || "Leave"}
                    subtitle={drawer ? `${drawer.leave_type} · ${drawer.start_date}${drawer.start_date === drawer.end_date ? '' : ' → '+drawer.end_date}` : ""}
                    testid="hr-decide-drawer">
        {drawer && (
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Type" value={drawer.leave_type} mono />
              <Field label="Status" value={drawer.status} mono />
              <Field label="Start" value={drawer.start_date} mono />
              <Field label="End" value={drawer.end_date} mono />
            </div>
            <div>
              <div className="micro-label">Note</div>
              <div className="mt-0.5 text-[12.5px] text-brand-ink">{drawer.note || "—"}</div>
            </div>
            {drawer.status === "pending" && canManage && (
              <div className="pt-3 border-t border-brand-rule flex items-center gap-2">
                <button className="btn-violet" onClick={() => decide(drawer, "approved")}
                        data-testid="leave-approve">
                  <Check size={13} /> Approve
                </button>
                <button className="btn-outline text-brand-coral border-brand-coral/40"
                        onClick={() => decide(drawer, "denied")}
                        data-testid="leave-deny">
                  <X size={13} /> Deny
                </button>
              </div>
            )}
          </div>
        )}
      </DetailDrawer>
    </div>
  );
}

function RequestLeaveForm({ companyId, onDone }) {
  const [leaveType, setLeaveType] = React.useState("holiday");
  const [start, setStart] = React.useState("");
  const [end, setEnd] = React.useState("");
  const [note, setNote] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  async function submit() {
    if (!start || !end || busy) return;
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/hr/leave`, {
        leave_type: leaveType, start_date: start, end_date: end, note,
      });
      toast.success("Created");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  }

  return (
    <div className="p-5 space-y-3">
      <div>
        <label className="micro-label">Type</label>
        <select className="field-input mt-1" value={leaveType}
                onChange={(e) => setLeaveType(e.target.value)}
                data-testid="leave-type">
          {LEAVE_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="micro-label">Start</label>
          <input type="date" className="field-input mt-1 mono-num" value={start}
                 onChange={(e) => setStart(e.target.value)} data-testid="leave-start" />
        </div>
        <div>
          <label className="micro-label">End</label>
          <input type="date" className="field-input mt-1 mono-num" value={end}
                 onChange={(e) => setEnd(e.target.value)} data-testid="leave-end" />
        </div>
      </div>
      <div>
        <label className="micro-label">Note</label>
        <textarea className="field-input mt-1 !h-24" value={note}
                  onChange={(e) => setNote(e.target.value)}
                  data-testid="leave-note" />
      </div>
      <button className="btn-violet w-full" onClick={submit} disabled={busy || !start || !end}
              data-testid="leave-submit">
        {busy ? "…" : "Submit request"}
      </button>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="micro-label">{label}</div>
      <div className={`mt-0.5 text-[12.5px] text-brand-ink ${mono ? 'mono-num' : ''}`}>{value || "—"}</div>
    </div>
  );
}
